import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { getSettings } from '../utils/settingsCache';

const Hero = () => {
  // Detect mobile once at mount — used to disable heavy inline CSS on trade cards
  const isMobile = useMemo(() =>
    window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);
  const [currentBuyTrade, setCurrentBuyTrade] = useState(null);
  const [currentSellTrade, setCurrentSellTrade] = useState(null);
  const [buyTradeKey, setBuyTradeKey] = useState(0);
  const [sellTradeKey, setSellTradeKey] = useState(0);

  // Hero text from settings
  const [heroTitle,    setHeroTitle]    = useState('SOLVIBE');
  const [heroSubtitle, setHeroSubtitle] = useState('$SVB');
  const [heroTagline,  setHeroTagline]  = useState('Decentralized by Design. Powered by Community.');
  const [vis,          setVis]          = useState({});
  const [sectionHidden, setSectionHidden] = useState(false);

  // New Holder Alert System
  const [isNewHolderAlert, setIsNewHolderAlert] = useState(false);
  const [newHolderData, setNewHolderData] = useState(null);
  const [logoGlowActive, setLogoGlowActive] = useState(false);

  const generateMockTrade = (isBuy = true) => {
    const smallQuantities = [25000, 45000, 78000, 95000, 125000, 165000, 210000, 285000, 320000, 375000, 425000, 490000, 520000, 575000, 620000, 680000, 720000];
    const mediumQuantities = [750000, 825000, 900000, 980000, 1050000, 1150000, 1250000, 1350000, 1450000];
    const largeQuantities = [1600000, 1750000, 1900000, 2000000];
    const walletAddresses = ['3nA...XkW', 'GxT...2vP', 'A9f...91Z', 'B2k...7mR', 'C8x...4jL', 'D5q...9nF', 'E1w...6pK', 'F7v...3sM', 'G4u...8tN', 'H9r...5yQ', 'I6e...2zX', 'J3t...7bC', 'K8y...4gV', 'L2i...9hB', 'M5o...6dW', 'N1p...3fE', 'P7a...8kY', 'Q4s...5lT', 'R9d...2mU', 'S6f...7nI', 'T2w...4mL', 'U7k...9pX', 'V5j...1qR', 'W8h...6sY', 'X3g...2tZ', 'Y9c...5uA', 'Z1b...8vC', 'Aa4...3wD', 'Bb7...6xE', 'Cc2...9yF'];

    const rand = Math.random();
    let quantity;
    if (rand < 0.7) quantity = smallQuantities[Math.floor(Math.random() * smallQuantities.length)];
    else if (rand < 0.95) quantity = mediumQuantities[Math.floor(Math.random() * mediumQuantities.length)];
    else quantity = largeQuantities[Math.floor(Math.random() * largeQuantities.length)];

    const wallet = walletAddresses[Math.floor(Math.random() * walletAddresses.length)];

    return { id: Date.now() + Math.random(), side: isBuy ? 'BUY' : 'SELL', quantity, wallet, timestamp: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) };
  };

  useEffect(() => {
    getSettings().then(data => {
      if (!data) return;
      if (data.hero_title)    setHeroTitle(data.hero_title);
      if (data.hero_subtitle) setHeroSubtitle(data.hero_subtitle);
      if (data.hero_tagline)  setHeroTagline(data.hero_tagline);
      if (data.visibility)    { try { setVis(JSON.parse(data.visibility)); } catch {} }
      if (data.section_hero === 'hidden') setSectionHidden(true);
    });
  }, []);

  useEffect(() => {
    const checkForNewHolders = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/new-holders/latest`);
        if (response.ok) {
          const data = await response.json();
          if (data.has_new_holder && !isNewHolderAlert) {
            setNewHolderData(data.holder);
            setIsNewHolderAlert(true);
            setLogoGlowActive(true);
            setTimeout(() => { setIsNewHolderAlert(false); setLogoGlowActive(false); setNewHolderData(null); }, 4500);
          }
        }
      } catch (error) {}
    };

    // Perf: don't fire during initial page load; poll gently afterwards.
    // (Backend route /api/new-holders/latest is currently not mounted — the
    //  poll is harmless but shouldn't compete with startup resources.)
    const firstCheck = setTimeout(checkForNewHolders, 20000);
    const interval = setInterval(checkForNewHolders, 60000);
    return () => { clearTimeout(firstCheck); clearInterval(interval); };
  }, [isNewHolderAlert]);

  useEffect(() => {
    const updateBuyTrade = () => { setCurrentBuyTrade(generateMockTrade(true)); setBuyTradeKey(prev => prev + 1); };
    const updateSellTrade = () => { setCurrentSellTrade(generateMockTrade(false)); setSellTradeKey(prev => prev + 1); };
    setTimeout(() => updateBuyTrade(), 1000);
    setTimeout(() => updateSellTrade(), 3000);
    const buyInterval = setInterval(updateBuyTrade, Math.random() * 2000 + 8000);
    const sellInterval = setInterval(updateSellTrade, Math.random() * 2000 + 8000);
    return () => { clearInterval(buyInterval); clearInterval(sellInterval); };
  }, []);

  const formatNumber = (num) => { if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'; if (num >= 1000) return (num / 1000).toFixed(1) + 'K'; return num.toFixed(2); };

  const NewHolderAlert = ({ isVisible, holderData }) => {
    if (!isVisible || !holderData) return null;
    return (
      <motion.div initial={{ opacity: 0, y: -40, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -30, scale: 0.9 }} transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], scale: { type: 'spring', stiffness: 250, damping: 18 } }} className="absolute -top-32 left-0 right-0 z-20 flex justify-center items-center">
        <div className="text-center">
          <motion.h2 initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.6, type: 'spring', stiffness: 350 }} className="text-3xl md:text-4xl font-sora font-bold bg-gradient-to-r from-neon-cyan via-solana-primary to-neon-green bg-clip-text text-transparent mb-4" style={{ filter: 'drop-shadow(0 0 25px rgba(20, 241, 149, 0.6))', textShadow: '0 0 40px rgba(153, 69, 255, 0.4)' }}>New Holder Joined!</motion.h2>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }} className="text-center">
            <p className="text-gray-300 font-inter text-base md:text-lg mb-2">Another wallet just joined the Vibe</p>
            <p className="text-neon-cyan font-mono text-sm">{holderData.wallet} • Welcome to $SVB</p>
          </motion.div>
        </div>
      </motion.div>
    );
  };

  const DownwardFlipTradeMessage = ({ trade, tradeKey }) => {
    if (!trade) return null;
    // Mobile: skip rotateX 3D flip (preserve-3d + blur forces expensive GPU layer on Android)
    const mobileAnim   = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.25 } };
    const desktopAnim  = { initial: { rotateX: -90, opacity: 0, scale: 0.9 }, animate: { rotateX: 0, opacity: 1, scale: 1 }, exit: { rotateX: 90, opacity: 0, scale: 0.9 }, transition: { duration: 1.0, ease: [0.25, 0.46, 0.45, 0.94], rotateX: { duration: 0.8, ease: 'easeInOut' }, opacity: { duration: 0.6, ease: 'easeOut' }, scale: { duration: 0.7, ease: 'easeOut' } } };
    const anim = isMobile ? mobileAnim : desktopAnim;
    const cardStyle = isMobile
      ? { boxShadow: '0 2px 8px rgba(239, 68, 68, 0.12)' }
      : { backdropFilter: 'blur(12px)', boxShadow: '0 4px 20px rgba(239, 68, 68, 0.15)', transformStyle: 'preserve-3d', transformOrigin: 'center center' };
    return (
      <motion.div key={tradeKey} {...anim} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-inter shadow-lg border-red-500/40 bg-red-500/15 text-red-400 shadow-red-500/20`} style={cardStyle}>
        <span className="font-bold text-xs">{trade.side}</span>
        <span className="text-white font-semibold text-xs">{formatNumber(trade.quantity)} $SVB</span>
        <span className="text-gray-400 text-xs">•</span>
        <span className="text-gray-400 font-mono text-xs">{trade.wallet}</span>
      </motion.div>
    );
  };

  const UpwardFlipTradeMessage = ({ trade, tradeKey }) => {
    if (!trade) return null;
    const mobileAnim   = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.25 } };
    const desktopAnim  = { initial: { rotateX: 90, opacity: 0, scale: 0.9 }, animate: { rotateX: 0, opacity: 1, scale: 1 }, exit: { rotateX: -90, opacity: 0, scale: 0.9 }, transition: { duration: 1.0, ease: [0.25, 0.46, 0.45, 0.94], rotateX: { duration: 0.8, ease: 'easeInOut' }, opacity: { duration: 0.6, ease: 'easeOut' }, scale: { duration: 0.7, ease: 'easeOut' } } };
    const anim = isMobile ? mobileAnim : desktopAnim;
    const cardStyle = isMobile
      ? { boxShadow: '0 2px 8px rgba(34, 197, 94, 0.12)' }
      : { backdropFilter: 'blur(12px)', boxShadow: '0 4px 20px rgba(34, 197, 94, 0.15)', transformStyle: 'preserve-3d', transformOrigin: 'center center' };
    return (
      <motion.div key={tradeKey} {...anim} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-inter shadow-lg border-green-500/40 bg-green-500/15 text-green-400 shadow-green-500/20`} style={cardStyle}>
        <span className="font-bold text-xs">{trade.side}</span>
        <span className="text-white font-semibold text-xs">{formatNumber(trade.quantity)} $SVB</span>
        <span className="text-gray-400 text-xs">•</span>
        <span className="text-gray-400 font-mono text-xs">{trade.wallet}</span>
      </motion.div>
    );
  };

  if (sectionHidden) return null;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="relative z-20 text-center px-4 max-w-6xl mx-auto">
        {/* Mobile: sofort sichtbar (LCP); Desktop: 0.5s Entrance-Effekt */}
        <motion.div initial={isMobile ? false : { opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: 'easeOut' }} className="mb-8 relative">
          <AnimatePresence>
            <NewHolderAlert isVisible={isNewHolderAlert} holderData={newHolderData} />
          </AnimatePresence>

          <div className="relative w-80 h-80 md:w-96 md:h-96 mx-auto">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Optimized 512px assets (35-44KB) instead of the 1.4MB master PNG — LCP fix.
                  WebP first, quantized PNG fallback. Same transparent look. */}
              <picture>
                <source srcSet="/logos/SolVibe-Logo-512.webp" type="image/webp" />
                <img
                  src="/logos/SolVibe-Logo-512.q.png"
                  alt="SolVibe official logo"
                  className="w-48 h-48 md:w-64 md:h-64 solvibe-logo-float"
                  width="512"
                  height="512"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  style={{ background: 'transparent' }}
                />
              </picture>
            </div>
          </div>
        </motion.div>

        {/* LCP-Fix: Die Tagline ist laut PageSpeed das LCP-Element. Auf Mobile
            (langsames Netz/CPU) rendert der Hero-Text SOFORT (initial=false),
            am Desktop bleibt der Stagger-Einblend-Effekt erhalten. */}
        {vis['hero_title'] !== false && <motion.h1 initial={isMobile ? false : { opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }} className="text-5xl md:text-7xl lg:text-8xl font-bold font-sora mb-6 bg-gradient-to-r from-solana-primary to-neon-cyan bg-clip-text text-transparent">{heroTitle}</motion.h1>}
        {vis['hero_subtitle'] !== false && <motion.div initial={isMobile ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="text-2xl md:text-3xl font-space font-medium mb-8 bg-gradient-to-r from-solana-primary to-neon-cyan bg-clip-text text-transparent">{heroSubtitle}</motion.div>}
        {vis['hero_tagline'] !== false && <motion.p initial={isMobile ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }} className="text-xl md:text-2xl lg:text-3xl font-inter mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed">{heroTagline}</motion.p>}
      </div>
    </section>
  );
};

export default React.memo(Hero);